/**
 * Riding out the auth worker's rate limit instead of failing on it, shared by
 * BOTH clients.
 *
 * The worker counts sign-in calls per IP (better-auth: 3 per 10s on each
 * `/sign-in/*` and `/sign-up/*` path, 3 per 60s on the code-sending routes),
 * and many real people share one IP: every web browser reaches the worker
 * through our Vercel proxy, whose egress IPs carry dozens of users a day each
 * (measured 2026-11-01), and a campus or carrier NAT does the same to the app.
 * So a 429 here is usually somebody ELSE's sign-in, not this user going too
 * fast — and showing them "Too many attempts" for it is a dead end they did
 * nothing to earn.
 *
 * What makes waiting it out safe: the limiter answers BEFORE the route runs.
 * A 429 sent no email, minted no session and used up none of a code's
 * attempts, so the same call can simply be made again — POSTs included.
 *
 * Per this package's rule (see ./index.ts) nothing here imports better-auth.
 */
import type { AuthErrorLike } from './auth-error';

/** The header better-auth puts on its 429 (`rateLimitResponse`), in seconds. */
const RETRY_AFTER_HEADER = 'x-retry-after';

/**
 * A `fetchOptions.onResponse` hook for either client's `createAuthClient`:
 * copies a 429's `X-Retry-After` into its JSON body as `retryAfter`.
 *
 * The `{ error }` a better-auth call resolves with is built from the body,
 * status and statusText alone — the header is dropped on the way, so without
 * this a caller cannot know how long to wait. Any other response passes
 * through untouched (`undefined` tells @better-fetch to keep it).
 */
export async function exposeRetryAfter({
  response,
}: {
  response: Response;
}): Promise<Response | undefined> {
  if (response.status !== 429) return undefined;
  const seconds = Number(response.headers.get(RETRY_AFTER_HEADER));
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(await response.clone().text());
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      body = parsed as Record<string, unknown>;
    }
  } catch {
    // Not JSON (a proxy's HTML error page): the status alone still says 429.
  }
  const headers = new Headers(response.headers);
  headers.set('content-type', 'application/json');
  headers.delete('content-length');
  return new Response(JSON.stringify({ ...body, retryAfter: seconds }), {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

/** Whether an auth call was refused by the rate limiter. */
export function isRateLimitedError(error: AuthErrorLike): boolean {
  return error?.status === 429;
}

/**
 * Longest single wait. The worker's `X-Retry-After` is the rule's WHOLE window
 * (60s for a code request), while its fixed window may already be most of the
 * way through — so trying again sooner usually gets through sooner, and an
 * early try costs nothing: it cannot push the window's end back.
 */
export const RATE_LIMIT_MAX_STEP_S = 15;
/** Wait used when a 429 says nothing about how long (no header reached us). */
export const RATE_LIMIT_DEFAULT_STEP_S = 10;
/**
 * Total patience before the user is shown the error after all. Covers one full
 * code-request window (60s) with room for the try that lands in the next one.
 */
export const RATE_LIMIT_BUDGET_S = 75;

export interface RateLimitWaitOptions {
  /** Called with the seconds about to be waited, before each wait. */
  onWait?: (seconds: number) => void;
  /** Aborting ends a wait at once and resolves with the 429 in hand. */
  signal?: AbortSignal;
  /** Injected in tests. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

/** How long to wait after this 429 before trying again. */
export function rateLimitStepSeconds(error: AuthErrorLike): number {
  const hinted = error?.retryAfter;
  const seconds =
    typeof hinted === 'number' && Number.isFinite(hinted) && hinted > 0
      ? hinted
      : RATE_LIMIT_DEFAULT_STEP_S;
  return Math.min(Math.ceil(seconds), RATE_LIMIT_MAX_STEP_S);
}

function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((done) => {
    if (signal?.aborted) return done();
    const id = setTimeout(finish, ms);
    function finish() {
      clearTimeout(id);
      signal?.removeEventListener('abort', finish);
      done();
    }
    signal?.addEventListener('abort', finish);
  });
}

/**
 * Run `attempt` (one better-auth call, `{ error }`-shaped) and, while it is
 * refused by the rate limiter, wait and run it again — up to
 * {@link RATE_LIMIT_BUDGET_S} in all. Anything other than a 429 is returned at
 * once, and so is the last 429 when the budget is spent or `signal` aborts:
 * the caller's usual error path then shows `auth.errors.rateLimited`.
 *
 * A rejection (offline, DNS) is not caught: it propagates as it always did.
 */
export async function waitOutRateLimit<R extends { error?: AuthErrorLike }>(
  attempt: () => Promise<R>,
  { onWait, signal, sleep = abortableSleep }: RateLimitWaitOptions = {},
): Promise<R> {
  let waited = 0;
  for (;;) {
    const result = await attempt();
    if (!isRateLimitedError(result.error) || signal?.aborted) return result;
    const step = rateLimitStepSeconds(result.error);
    if (waited + step > RATE_LIMIT_BUDGET_S) return result;
    onWait?.(step);
    await sleep(step * 1000, signal);
    if (signal?.aborted) return result;
    waited += step;
  }
}
